import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PatientEditComponent } from 'app/app-modules/common/add-patient';
import { Button } from 'primeng/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SavePatientModalData } from './save-patient-modal-data';
import { Patient, PatientFileModel } from 'app/core/models';
import { FormGroup } from '@angular/forms';
import { PatientApiClient } from 'app/core/api-clients/patient.client';
import { firstValueFrom } from 'rxjs';
import { parseISO } from 'date-fns';
import { PatientFilesSaveService, PatientFileUploadState } from 'app/app-modules/common/add-patient';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'wl-save-patient-model',
  standalone: true,
  imports: [CommonModule, PatientEditComponent, Button, MatIconModule, TranslatePipe],
  templateUrl: './save-patient-modal.component.html',
  styleUrl: './save-patient-modal.component.scss',
})
export class SavePatientModalComponent implements OnInit {
  private readonly _config = inject<DynamicDialogConfig<SavePatientModalData>>(DynamicDialogConfig);
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _patientApiClient = inject(PatientApiClient);
  private readonly _messageService = inject(MessageService);
  private readonly _translate = inject(TranslateService);

  isSaving = false;
  patient?: Patient | null | undefined = this._config.data?.patient;
  form: FormGroup | null = null;
  patientFiles: PatientFileModel[] | null = null;
  patientFileState: PatientFileUploadState | null = null;
  canSaveFiles = true;

  private readonly _patientFilesSaveService = inject(PatientFilesSaveService);

  get showMomentDirectPatientFiles(): boolean {
    return !!this.patient?.isMomentDirect;
  }

  async ngOnInit(): Promise<void> {
    if (this.patient?.id) {
      this.patientFiles = await firstValueFrom(this._patientApiClient.getFiles(this.patient.id));
    }
  }

  async onSave() {
    const { isSaving, form, patient } = this;

    if (isSaving || !form || form.invalid || !this.canSaveFiles) return;

    this.isSaving = true;
    const formValue = form.value;
    const updatedPatient = {
      ...(patient ?? {}),
      ...formValue,
      isMomentDirect: this.showMomentDirectPatientFiles ? true : (patient?.isMomentDirect ?? formValue.isMomentDirect),
      dateOfBirth: this.tryParseDate(formValue.dateOfBirth) ?? null,
    };

    try {
      const savedPatient = await firstValueFrom(this._patientApiClient.save(updatedPatient));
      await this._patientFilesSaveService.save(savedPatient.id, this.patientFileState);

      if (savedPatient) {
        this._ref.close({ saved: true });
      }
    } catch (error) {
      console.error(error);
      this._messageService.add({
        severity: 'error',
        summary: this._translate.instant('general.error'),
        detail: this._translate.instant('general.error-occurred'),
      });
    } finally {
      this.isSaving = false;
    }
  }

  private tryParseDate(v: any) {
    try {
      if (!v) {
        return null;
      }

      if (v instanceof Date) {
        return Number.isNaN(v.getTime()) ? null : v;
      }

      const parsedDate = parseISO(v);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  onClose() {
    this._ref.close({ saved: false });
  }

  protected onFormChange(form: FormGroup | null) {
    this.form = form;
    this.updateCanSaveFiles();
  }

  protected onPatientFilesChanged(state: PatientFileUploadState | null): void {
    this.patientFileState = state;
    this.updateCanSaveFiles();
  }

  private updateCanSaveFiles(): void {
    const needsPatientFiles = !!this.form?.value?.isMomentDirect;
    this.canSaveFiles = !needsPatientFiles || !!this.patientFileState?.isValid;
  }
}
